import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Bell, X, CheckCircle, Shield, Settings, Clock } from 'lucide-react';
import { useDashboard } from '../../lib/api/hooks';

type Severity = 'info' | 'warning' | 'critical';

interface LiveNotification {
  id: string;
  severity: Severity;
  title: string;
  message: string;
  source: 'setup' | 'review';
  time: string | null;
}

// Notifications are derived from real control-plane signals: org-setup alerts (backups / immutable
// audit not configured) and items flagged for review (blocked / high-exposure requests).
export function AdminNotifications() {
  const { data: live } = useDashboard();
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState<string[]>([]);

  const alerts = live?.alerts ?? [];
  const approvals = live?.approvals ?? [];

  const items: LiveNotification[] = [
    ...alerts.map((a) => ({
      id: `alert-${a.key}`,
      severity: (a.severity === 'high' ? 'warning' : 'critical') as Severity,
      title: a.title,
      message: a.detail,
      source: 'setup' as const,
      time: null,
    })),
    ...approvals.map((a) => ({
      id: `review-${a.id}`,
      severity: (a.risk === 'critical' ? 'critical' : 'warning') as Severity,
      title: a.title,
      message: a.action,
      source: 'review' as const,
      time: a.createdAt,
    })),
  ];

  const visible = items.filter((n) => !dismissed.includes(n.id));
  const count = visible.length;

  const sevColor = (s: Severity) =>
    s === 'critical'
      ? 'bg-red-100 text-red-800'
      : s === 'warning'
        ? 'bg-yellow-100 text-yellow-800'
        : 'bg-blue-100 text-blue-800';

  return (
    <>
      <div className="relative">
        <Button variant="outline" size="sm" className="gap-2 relative" onClick={() => setIsOpen(true)}>
          <Bell className="w-4 h-4" />
          <span className="hidden md:inline">Notifications</span>
          {count > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 bg-red-600">
              {count}
            </Badge>
          )}
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                <DialogTitle>Notifications</DialogTitle>
                {count > 0 && <Badge variant="destructive">{count} active</Badge>}
              </div>
              {count > 0 && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => setDismissed(items.map((i) => i.id))}>
                  Dismiss All
                </Button>
              )}
            </div>
            <DialogDescription>Live setup alerts and items flagged for review</DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] pr-4">
            {visible.length === 0 ? (
              <div className="py-12 text-center">
                <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-20" />
                <p className="text-muted-foreground">All clear. No active notifications</p>
              </div>
            ) : (
              <div className="space-y-3">
                {visible.map((n) => {
                  const Icon = n.source === 'setup' ? Settings : Shield;
                  return (
                    <Card key={n.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${sevColor(n.severity)}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="font-semibold text-sm">{n.title}</h4>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setDismissed((p) => [...p, n.id])}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            <p className="text-sm text-muted-foreground">{n.message}</p>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-2">
                              <span className="flex items-center gap-1">
                                {n.source === 'setup' ? <Settings className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                                {n.source === 'setup' ? 'Setup' : 'Review queue'}
                              </span>
                              {n.time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(n.time).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
